import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography, CircularProgress, Alert,
  TextField, Collapse, IconButton, Chip, InputAdornment
} from '@mui/material';
import {
  KeyboardArrowUp as UpIcon,
  KeyboardArrowDown as DownIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { monthlySurgicalOperations } from '../BaseURL';

const AGE_GROUPS = [
  { key: '0-28 Day',   label: '<28days'  },
  { key: '01-11 Mon.', label: '1-11mths' },
  { key: '01-4 Yrs',  label: '1-4'      },
  { key: '05-09 Yrs', label: '5-9'      },
  { key: '10-14 Yrs', label: '10-14'    },
  { key: '15-17 Yrs', label: '15-17'    },
  { key: '18-19 Yrs', label: '18-19'    },
  { key: '20-34 Yrs', label: '20-34'    },
  { key: '35-49 Yrs', label: '35-49'    },
  { key: '50-59 Yrs', label: '50-59'    },
  { key: '60-69 Yrs', label: '60-69'    },
  { key: '70 + Yrs',  label: '70+'      },
];

const hCell = (extra = {}) => ({
  bgcolor: '#1a3c5e', color: '#fff', fontWeight: 700,
  fontSize: '0.7rem', textAlign: 'center',
  border: '1px solid rgba(255,255,255,0.15)',
  whiteSpace: 'nowrap', py: 0.6, px: 0.5,
  ...extra,
});

const dCell = (extra = {}) => ({
  textAlign: 'center', fontSize: '0.72rem',
  border: '1px solid #e0e0e0', py: 0.4, px: 0.5,
  ...extra,
});

function SectionHeader({ label, open, onToggle, count, matchCount, globalFilter }) {
  return (
    <Box
      onClick={onToggle}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1,
        bgcolor: '#1a3c5e', color: '#fff', px: 1.5, py: 0.8,
        cursor: 'pointer', userSelect: 'none',
        '&:hover': { bgcolor: '#24527a' },
      }}
    >
      <IconButton size="small" sx={{ color: '#fff', p: 0.2 }}>
        {open ? <UpIcon fontSize="small" /> : <DownIcon fontSize="small" />}
      </IconButton>
      <Typography variant="body2" fontWeight={700} sx={{ flexGrow: 1 }}>
        {label}
      </Typography>
      {globalFilter && matchCount !== count && (
        <Chip
          label={`${matchCount} match`}
          size="small"
          sx={{ bgcolor: '#f59e0b', color: '#000', height: 18, fontSize: '0.65rem', mr: 0.5 }}
        />
      )}
      <Chip
        label={count}
        size="small"
        sx={{ bgcolor: '#2e6da4', color: '#fff', height: 18, fontSize: '0.65rem' }}
      />
    </Box>
  );
}

function SurgicalSection({ category, procedures, dataMap, globalFilter }) {
  const [open, setOpen]     = useState(true);
  const [filter, setFilter] = useState('');

  const filtered = useMemo(() => {
    let result = procedures;
    if (globalFilter.trim()) {
      result = result.filter(p =>
        p.toLowerCase().includes(globalFilter.toLowerCase())
      );
    }
    if (filter.trim()) {
      result = result.filter(p =>
        p.toLowerCase().includes(filter.toLowerCase())
      );
    }
    return result;
  }, [procedures, filter, globalFilter]);

  const hasGlobalMatch = useMemo(() => {
    if (!globalFilter.trim()) return false;
    return procedures.some(p =>
      p.toLowerCase().includes(globalFilter.toLowerCase())
    );
  }, [procedures, globalFilter]);

  const isOpen = open || hasGlobalMatch;

  const getVal = (proc, gender, ageKey) =>
    dataMap[proc]?.[gender]?.[ageKey] ?? 0;

  const rowTotal = (proc, gender) =>
    AGE_GROUPS.reduce((s, ag) => s + getVal(proc, gender, ag.key), 0);

  const grandTotal = (proc) =>
    rowTotal(proc, 'Male') + rowTotal(proc, 'Female');

  const sectionColTotal = (gender, ageKey) =>
    filtered.reduce((s, p) => s + getVal(p, gender, ageKey), 0);

  const sectionGenderTotal = (gender) =>
    filtered.reduce((s, p) => s + rowTotal(p, gender), 0);

  const sectionGrandTotal = () =>
    filtered.reduce((s, p) => s + grandTotal(p), 0);

  if (globalFilter.trim() && filtered.length === 0) return null;

  return (
    <Box sx={{ mb: 0.5 }}>
      <SectionHeader
        label={category}
        open={isOpen}
        onToggle={() => setOpen(p => !p)}
        count={procedures.length}
        matchCount={filtered.length}
        globalFilter={globalFilter}
      />

      <Collapse in={isOpen}>
        {/* Section filter */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          px: 1.5, py: 0.6, bgcolor: '#f5f5f5',
          borderBottom: '1px solid #e0e0e0'
        }}>
          <FilterIcon sx={{ fontSize: 14, color: '#90a4ae' }} />
          <TextField
            size="small"
            variant="standard"
            placeholder="Type here to filter rows in this section"
            value={filter}
            onChange={e => setFilter(e.target.value)}
            InputProps={{ disableUnderline: true }}
            sx={{ flex: 1 }}
            inputProps={{ style: { fontSize: '0.75rem', padding: '2px 0' } }}
          />
          {filter && (
            <IconButton size="small" onClick={() => setFilter('')} sx={{ p: 0.2 }}>
              <ClearIcon sx={{ fontSize: 14, color: '#90a4ae' }} />
            </IconButton>
          )}
        </Box>

        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 1600 }}>
            <colgroup>
              <col style={{ width: 220 }} />
              {AGE_GROUPS.map((_, i) => <col key={`mc${i}`} style={{ width: 50 }} />)}
              <col style={{ width: 50 }} />
              {AGE_GROUPS.map((_, i) => <col key={`fc${i}`} style={{ width: 50 }} />)}
              <col style={{ width: 50 }} />
              <col style={{ width: 55 }} />
            </colgroup>

            <TableHead>
              <TableRow>
                <TableCell rowSpan={2} sx={hCell({ textAlign: 'left', pl: 1 })}>
                  Surgical Procedure
                </TableCell>
                <TableCell colSpan={AGE_GROUPS.length + 1} sx={hCell({ bgcolor: '#1565c0' })}>
                  Male
                </TableCell>
                <TableCell colSpan={AGE_GROUPS.length + 1} sx={hCell({ bgcolor: '#880e4f' })}>
                  Female
                </TableCell>
                <TableCell rowSpan={2} sx={hCell({ bgcolor: '#2e7d32' })}>
                  Total
                </TableCell>
              </TableRow>

              <TableRow>
                {AGE_GROUPS.map(ag => (
                  <TableCell key={`mh-${ag.key}`} sx={hCell({ bgcolor: '#1976d2' })}>
                    {ag.label}
                  </TableCell>
                ))}
                <TableCell sx={hCell({ bgcolor: '#1565c0' })}>Sub</TableCell>
                {AGE_GROUPS.map(ag => (
                  <TableCell key={`fh-${ag.key}`} sx={hCell({ bgcolor: '#ad1457' })}>
                    {ag.label}
                  </TableCell>
                ))}
                <TableCell sx={hCell({ bgcolor: '#880e4f' })}>Sub</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={AGE_GROUPS.length * 2 + 4}
                    sx={{ textAlign: 'center', py: 2, color: '#90a4ae', fontSize: '0.75rem' }}
                  >
                    No matching procedures found
                  </TableCell>
                </TableRow>
              )}

              {filtered.map((proc, idx) => {
                const mTotal = rowTotal(proc, 'Male');
                const fTotal = rowTotal(proc, 'Female');
                const grand  = mTotal + fTotal;

                const isHighlighted = globalFilter.trim() &&
                  proc.toLowerCase().includes(globalFilter.toLowerCase());

                return (
                  <TableRow
                    key={proc}
                    sx={{
                      bgcolor: isHighlighted
                        ? '#fff8e1'
                        : idx % 2 === 0 ? '#fff' : '#fafafa',
                      '&:hover': { bgcolor: '#e8f4fd' },
                    }}
                  >
                    <TableCell sx={{
                      fontSize: '0.72rem',
                      fontWeight: isHighlighted ? 700 : 500,
                      border: '1px solid #e0e0e0', pl: 1, py: 0.4,
                      whiteSpace: 'normal', lineHeight: 1.3,
                      color: isHighlighted ? '#b45309' : 'inherit',
                    }}>
                      {proc}
                    </TableCell>

                    {AGE_GROUPS.map(ag => (
                      <TableCell key={`m-${ag.key}`} sx={dCell()}>
                        {getVal(proc, 'Male', ag.key)}
                      </TableCell>
                    ))}
                    <TableCell sx={dCell({ fontWeight: 700, bgcolor: '#e3f2fd' })}>
                      {mTotal}
                    </TableCell>

                    {AGE_GROUPS.map(ag => (
                      <TableCell key={`f-${ag.key}`} sx={dCell()}>
                        {getVal(proc, 'Female', ag.key)}
                      </TableCell>
                    ))}
                    <TableCell sx={dCell({ fontWeight: 700, bgcolor: '#fce4ec' })}>
                      {fTotal}
                    </TableCell>

                    <TableCell sx={dCell({ fontWeight: 700, bgcolor: '#e8f5e9' })}>
                      {grand}
                    </TableCell>
                  </TableRow>
                );
              })}

              {filtered.length > 0 && (
                <TableRow sx={{ bgcolor: '#e8eaf6' }}>
                  <TableCell sx={{
                    fontSize: '0.7rem', fontWeight: 700,
                    border: '1px solid #c5cae9', pl: 1, py: 0.5
                  }}>
                    Section Total
                  </TableCell>
                  {AGE_GROUPS.map(ag => (
                    <TableCell key={`st-m-${ag.key}`} sx={dCell({ fontWeight: 700 })}>
                      {sectionColTotal('Male', ag.key)}
                    </TableCell>
                  ))}
                  <TableCell sx={dCell({ fontWeight: 700, bgcolor: '#e3f2fd' })}>
                    {sectionGenderTotal('Male')}
                  </TableCell>
                  {AGE_GROUPS.map(ag => (
                    <TableCell key={`st-f-${ag.key}`} sx={dCell({ fontWeight: 700 })}>
                      {sectionColTotal('Female', ag.key)}
                    </TableCell>
                  ))}
                  <TableCell sx={dCell({ fontWeight: 700, bgcolor: '#fce4ec' })}>
                    {sectionGenderTotal('Female')}
                  </TableCell>
                  <TableCell sx={dCell({ fontWeight: 700, bgcolor: '#e8f5e9' })}>
                    {sectionGrandTotal()}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Collapse>
    </Box>
  );
}

export default function MonthlySurgicalOperationsReport({ period }) {
  const [sections, setSections]         = useState([]);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState(null);
  const [globalFilter, setGlobalFilter] = useState('');

  useEffect(() => {
    if (!period) return;

    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        setGlobalFilter('');

        const res    = await fetch(`${monthlySurgicalOperations}?period=${period}`);
        const result = await res.json();

        if (!result.success) throw new Error(result.message || 'Failed to load');

        setSections(processData(result.data));
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [period]);

  const toTitle = (str) =>
    String(str || '')
      .trim()
      .toLowerCase()
      .replace(/\b\w/g, c => c.toUpperCase());

  const processData = (rows) => {
    const map = {};

    rows.forEach(({ category, procedure_name, gender, age_group, count }) => {
      const normCat  = toTitle(category);
      const normProc = toTitle(procedure_name);
      const normAge  = String(age_group || '').trim();

      const rawGender  = String(gender || '').trim().toLowerCase();
      const normGender =
        rawGender === 'male'   || rawGender === 'm' ? 'Male'   :
        rawGender === 'female' || rawGender === 'f' ? 'Female' :
        String(gender || '').trim();

      if (!normCat || !normProc) return;

      if (!map[normCat])                         map[normCat] = {};
      if (!map[normCat][normProc])               map[normCat][normProc] = {};
      if (!map[normCat][normProc][normGender])   map[normCat][normProc][normGender] = {};

      map[normCat][normProc][normGender][normAge] =
        (map[normCat][normProc][normGender][normAge] || 0) + Number(count);
    });

    return Object.entries(map)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([category, procMap]) => ({
        category,
        procedures: Object.keys(procMap).sort(),
        dataMap:    procMap,
      }));
  };

  const totalMatches = useMemo(() => {
    if (!globalFilter.trim()) return null;
    return sections.reduce((sum, sec) =>
      sum + sec.procedures.filter(p =>
        p.toLowerCase().includes(globalFilter.toLowerCase())
      ).length, 0
    );
  }, [sections, globalFilter]);

  const totalProcedures = sections.reduce((s, sec) => s + sec.procedures.length, 0);

  if (!period) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary" variant="body2">
          Please select a period to view surgical operations data
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (sections.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          No surgical operations data found for the selected period.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>

      {/* ── Top bar ── */}
      <Box sx={{
        display: 'flex', alignItems: 'center',
        gap: 2, mb: 1.5, flexWrap: 'wrap'
      }}>
        <Chip
          label={`${sections.length} MDC categories`}
          size="small"
          sx={{ bgcolor: '#1a3c5e', color: '#fff', fontSize: '0.7rem' }}
        />
        <Chip
          label={`${totalProcedures} procedures`}
          size="small"
          sx={{ bgcolor: '#1565c0', color: '#fff', fontSize: '0.7rem' }}
        />

        <Box sx={{ ml: 'auto', minWidth: 300 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search all procedures across all MDC categories…"
            value={globalFilter}
            onChange={e => setGlobalFilter(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: '#90a4ae' }} />
                </InputAdornment>
              ),
              endAdornment: globalFilter ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setGlobalFilter('')}>
                    <ClearIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{
              bgcolor: '#fff',
              borderRadius: 1,
              '& .MuiOutlinedInput-root': { fontSize: '0.8rem' },
            }}
          />
        </Box>

        {totalMatches !== null && (
          <Chip
            label={`${totalMatches} match${totalMatches !== 1 ? 'es' : ''}`}
            size="small"
            sx={{
              bgcolor: totalMatches > 0 ? '#b45309' : '#bdbdbd',
              color: '#fff', fontSize: '0.7rem'
            }}
          />
        )}
      </Box>

      {/* ── MDC Sections ── */}
      <Paper elevation={1} sx={{ overflow: 'hidden' }}>
        {sections.map(sec => (
          <SurgicalSection
            key={sec.category}
            category={sec.category}
            procedures={sec.procedures}
            dataMap={sec.dataMap}
            globalFilter={globalFilter}
          />
        ))}
      </Paper>
    </Box>
  );
}